import { BASE_URL } from "../../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";

// ============================================================
//  INTERFACES
// ============================================================

export interface InvoiceData {
  invoiceId: string;
  invoiceNumber: string;
  organizationId: string;
  organizationName: string;
  invoiceDate: string;
  periodStart: string;
  periodEnd: string;
  totalPages: number;
  pricePerPagePence: number;
  totalAmount: number;
  status: string;
  notes?: string;
  createdBy: string;
  createdDate: string;
  paidDate?: string;
  dueDate?: string;
}

export interface PageUsageData {
  organizationId: string;
  organizationName: string;
  totalPages: number;
  billedPages: number;
  unbilledPages: number;
  unbilledAmount: number;
  startDate?: string;
  endDate?: string;
}

export interface PageUsageDetail {
  id: string;
  fileImportId: string;
  subRequestId?: string;
  fileName?: string;
  documentType?: string;
  pagesExtracted: number;
  pagesClassified: number;
  pricePerExtractionPage: number;
  pricePerClassificationPage: number;
  amount: number;
  isBilled: boolean;
  createdDate: string;
  classificationDate?: string;
  extractionDate?: string;
  // Enhanced fields
  processType?: string;
  classificationModelId?: string;
  extractionModelId?: string;
  totalFormsInPack?: number;
  w8BenCount?: number;
  w8BenECount?: number;
  w8ImyCount?: number;
  w8ExpCount?: number;
  w8EciCount?: number;
  f8233Count?: number;
  w9Count?: number;
  selfCertEntityCount?: number;
  selfCertIndividualCount?: number;
  withholdingCount?: number;
  idDocumentCount?: number;
  otherCount?: number;
  uploadType?: string;
  confidenceScore?: number;
}

export interface ProcessBreakdown {
  totalPages: number;
  billedPages: number;
  unbilledPages: number;
  unbilledAmount: number;
  pricePerPage: number;
  // Document-type page breakdown
  w8BenPages: number;
  w8BenEPages: number;
  w8ImyPages: number;
  w8ExpPages: number;
  w8EciPages: number;
  f8233Pages: number;
  w9Pages: number;
  selfCertEntityPages: number;
  selfCertIndividualPages: number;
  withholdingPages: number;
  idDocumentPages: number;
  otherPages: number;
}

export interface InvoiceSummary {
  totalInvoices: number;
  totalRevenue: number;
  pendingRevenue: number;
  overdueRevenue: number;
  totalPagesProcessed: number;
  unbilledPages: number;
  unbilledAmount: number;
  // Separate classification & extraction breakdowns
  classification?: ProcessBreakdown;
  extraction?: ProcessBreakdown;
  // Overall counts
  packCount?: number;
  individualCount?: number;
}

export interface GenerateInvoiceRequest {
  organizationId: string;
  startDate: string;
  endDate: string;
  pricePerExtractionPage?: number;
  pricePerClassificationPage?: number;
  createdBy?: string;
  notes?: string;
}

// ============================================================
//  PRICING CONSTANTS
// ============================================================

export const DEFAULT_EXTRACTION_PRICE = 0.1; // £0.10 per page default
export const DEFAULT_CLASSIFICATION_PRICE = 0; // free by default

// Currency options available to super admin
export interface CurrencyOption {
  code: string;
  symbol: string;
  name: string;
}

export const CURRENCY_OPTIONS: CurrencyOption[] = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
];

export const DEFAULT_CURRENCY = CURRENCY_OPTIONS[0]; // GBP

// ============================================================
//  HELPERS
// ============================================================

const getAuthHeaders = () => {
  const userData = getUserIdFromLocalStorage();
  return {
    Authorization: `Bearer ${userData.token}`,
    "Content-Type": "application/json",
  };
};

/** Format amount with the given currency symbol (defaults to £) */
export const formatAmountWithCurrency = (amount: number, symbol: string = "$"): string =>
  `${symbol}${Number(amount).toFixed(2)}`;

export const formatAmount = (amount: number): string => `$${Number(amount).toFixed(2)}`;

export const calculateAmount = (totalPages: number, pricePerPage: number = 0): number =>
  totalPages * pricePerPage;

// ============================================================
//  INVOICE SUMMARY
// ============================================================

export const getInvoiceSummary = async (): Promise<InvoiceSummary> => {
  const response = await fetch(`${BASE_URL}/Invoice/summary`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to fetch invoice summary: ${response.status}`);
  return response.json();
};

// ============================================================
//  PAGE USAGE
// ============================================================

export const getPageUsageByOrganization = async (
  organizationId: string,
  startDate?: string,
  endDate?: string
): Promise<PageUsageData> => {
  const params = new URLSearchParams();
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);

  const response = await fetch(
    `${BASE_URL}/Invoice/page-usage/${organizationId}?${params.toString()}`,
    { method: "GET", headers: getAuthHeaders() }
  );
  if (!response.ok) throw new Error(`Failed to fetch page usage: ${response.status}`);
  return response.json();
};

export const getPageUsageDetails = async (
  organizationId: string,
  startDate?: string,
  endDate?: string,
  unbilledOnly?: boolean
): Promise<PageUsageDetail[]> => {
  const params = new URLSearchParams();
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (unbilledOnly) params.append("unbilledOnly", "true");

  const response = await fetch(
    `${BASE_URL}/Invoice/page-usage/${organizationId}/details?${params.toString()}`,
    { method: "GET", headers: getAuthHeaders() }
  );
  if (!response.ok) throw new Error(`Failed to fetch page usage details: ${response.status}`);
  return response.json();
};

// ============================================================
//  INVOICES - CRUD
// ============================================================

export const getAllInvoices = async (
  startDate?: string,
  endDate?: string,
  status?: string
): Promise<InvoiceData[]> => {
  const params = new URLSearchParams();
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (status) params.append("status", status);

  const response = await fetch(`${BASE_URL}/Invoice/all?${params.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to fetch all invoices: ${response.status}`);
  return response.json();
};

export const getInvoicesByOrganization = async (organizationId: string): Promise<InvoiceData[]> => {
  const response = await fetch(`${BASE_URL}/Invoice/organization/${organizationId}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to fetch invoices: ${response.status}`);
  return response.json();
};

export const getInvoiceById = async (invoiceId: string): Promise<InvoiceData> => {
  const response = await fetch(`${BASE_URL}/Invoice/${invoiceId}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to fetch invoice: ${response.status}`);
  return response.json();
};

export const generateInvoice = async (data: GenerateInvoiceRequest): Promise<InvoiceData> => {
  const userData = getUserIdFromLocalStorage();
  const body = {
    ...data,
    pricePerExtractionPage:
      data.pricePerExtractionPage ?? Math.round(DEFAULT_EXTRACTION_PRICE * 100),
    pricePerClassificationPage:
      data.pricePerClassificationPage ?? Math.round(DEFAULT_CLASSIFICATION_PRICE * 100),
    createdBy: data.createdBy || userData.email || userData.userName,
  };

  const response = await fetch(`${BASE_URL}/Invoice/generate`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Failed to generate invoice: ${response.status}`);
  }
  return response.json();
};

export const updateInvoice = async (
  invoiceId: string,
  data: Record<string, any>
): Promise<void> => {
  const response = await fetch(`${BASE_URL}/Invoice/${invoiceId}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(`Failed to update invoice: ${response.status}`);
};

export const updateInvoiceStatus = async (invoiceId: string, status: string): Promise<void> => {
  const response = await fetch(`${BASE_URL}/Invoice/${invoiceId}/status`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  });
  if (!response.ok) throw new Error(`Failed to update invoice status: ${response.status}`);
};

export const deleteInvoice = async (invoiceId: string, reason: string): Promise<void> => {
  const response = await fetch(
    `${BASE_URL}/Invoice/${invoiceId}?reason=${encodeURIComponent(reason)}`,
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    }
  );
  if (!response.ok) throw new Error(`Failed to delete invoice: ${response.status}`);
};

// ============================================================
//  PDF DOWNLOAD
// ============================================================

export const downloadInvoicePDF = async (invoiceId: string): Promise<Blob> => {
  const response = await fetch(`${BASE_URL}/Invoice/${invoiceId}/pdf`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
    },
  });

  if (!response.ok) throw new Error(`Failed to download invoice PDF: ${response.status}`);
  return response.blob();
};
